import { makeAutoObservable, runInAction } from "mobx";
import { TYPE, rootContainer } from "../../../services/container/root";
import PolicyService from "../../../services/api/policy-service";
import {
    CONDITION_GROUP_OPERATOR,
    CONDITION_OPERATORS,
    DISPLAY_FEE_OPTION,
    IPolicyAddressRequirements,
    IPolicyCategoryToResolutionMap,
    IPolicyCondition,
    IPolicyConditionGroup,
    IPolicyRule,
    IPolicyRulesSettingsPreset,
    IReturnMethodPreset,
    POLICY_RULE_CATEGORY,
    buildDuplicatedPolicyLocalizationKeys,
    buildPolicyItemIneligibilityReasonClientKey,
} from "../policy-types";
import PolicyStore, { IPolicyRuleChanges } from "./policy-store";
import { RESOLUTION_ID, RESOLUTION_TYPE } from "../../resolutions/constants";
import { NOTE_TYPE } from "../../../components/note/note";
import {
    APPLY_ON,
    APPROVAL_PROCESS,
    AUTO_MARK_DONE,
    AUTO_PROCESS_EXECUTE,
    CHARGE_FEES,
    REFUND_POLICIES_NOTE_TYPE,
    SINCE_UNIT,
    USED_FOR_OPTIONS,
} from "../constants";
import currentPortalStore from "../../../stores/current-portal-store";
import { uuid } from "../../../services/helpers";
import {
    AUTO_PROCESS_TRIGGER,
    AUTO_PROCESS_VALIDATED_QUALIFIER,
    RETURN_LABEL_FEE_PAYMENT,
} from "../../../models/User";
import AggregatorsInfoStore from "../../return-methods/stores/aggregators-info-store";
import dashboardSettingsStore from "../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../models/ProfileCapabilities";
import rmsMixpanelStore from "../../../stores/rms-mixpanel-store";
import { POLICY_RULES_MIXPANEL_DROP_ADD_RULE } from "../metrics/policy-rules-mixpanel-events";

interface IPolicyStoreByCategoryList {
    [POLICY_RULE_CATEGORY.EXCHANGE]: PolicyStore[];
    [POLICY_RULE_CATEGORY.RETURN]: PolicyStore[];
    [POLICY_RULE_CATEGORY.ORDER_MODIFICATION]: PolicyStore[];
}

export class PolicyListStore {
    private _isLoading: boolean = false;
    private _isLoaded: boolean = false;
    private _policyRuleList: PolicyStore[] = [];
    private _settings: IPolicyRulesSettingsPreset =
        {} as IPolicyRulesSettingsPreset;
    private _aggregatorsInfoStore?: AggregatorsInfoStore;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private get policyService(): PolicyService {
        return rootContainer.get<PolicyService>(TYPE.PolicyService);
    }

    public get isLoading(): boolean {
        return this._isLoading;
    }

    public get isLoaded(): boolean {
        return this._isLoaded;
    }

    public get settings(): IPolicyRulesSettingsPreset {
        return this._settings;
    }

    private get _policyCategoryToResolutionIdsMap(): IPolicyCategoryToResolutionMap {
        return this.settings.policy_category_to_resolution_ids_map;
    }

    public get hasGlobalEDirectRefundIntegration(): boolean {
        return this.settings.has_global_e_direct_refund_integration;
    }

    public get policyListByCategory(): IPolicyStoreByCategoryList {
        const listByCategory = {} as IPolicyStoreByCategoryList;

        if (!this._policyCategoryToResolutionIdsMap) {
            return listByCategory;
        }

        Object.values(POLICY_RULE_CATEGORY).forEach((category) => {
            const map =
                this._policyCategoryToResolutionIdsMap[
                    category as POLICY_RULE_CATEGORY
                ];

            listByCategory[category as POLICY_RULE_CATEGORY] =
                this._policyRuleList
                    .slice()
                    .sort((p1, p2) => p1.name.localeCompare(p2.name)) // sort policies by name
                    .sort((p1, p2) => {
                        // sort policies by is_enabled, enabled first
                        if (p1.isEnabled > p2.isEnabled) {
                            return -1;
                        }
                        if (p1.isEnabled < p2.isEnabled) {
                            return 1;
                        }
                        return 0;
                    })
                    .filter((policyRule) => {
                        if (map && map.exclude_resolution_ids.length) {
                            return (
                                !policyRule.selectedResolutionId ||
                                !map.exclude_resolution_ids.includes(
                                    policyRule.selectedResolutionId
                                )
                            );
                        }

                        /** if no resolution selected we can not put this rule into any category */
                        if (!policyRule.selectedResolutionId) {
                            return false;
                        }

                        if (
                            map &&
                            map.include_resolution_ids.length &&
                            policyRule.selectedResolutionId
                        ) {
                            return map.include_resolution_ids.includes(
                                policyRule.selectedResolutionId
                            );
                        }

                        return true;
                    });
        });

        return listByCategory;
    }

    /**
     * Returns the policy rule category list that contains this policy instance
     */
    public getCategoryForPolicy(
        policy: PolicyStore
    ): POLICY_RULE_CATEGORY | undefined {
        for (const category of Object.values(POLICY_RULE_CATEGORY)) {
            const list = this.policyListByCategory[category];
            if (list?.some((p) => p.isIdEquals(policy.id))) {
                return category;
            }
        }
        return undefined;
    }

    /**
     * Category for a resolution id using the same rules as policyListByCategory filters.
     * Used when the policy store may no longer be in the list (e.g. after a full reload).
     */
    public getCategoryForResolutionId(
        resolutionId: RESOLUTION_ID | undefined | null
    ): POLICY_RULE_CATEGORY | undefined {
        if (!resolutionId || !this._policyCategoryToResolutionIdsMap) {
            return undefined;
        }
        for (const category of Object.values(POLICY_RULE_CATEGORY)) {
            const map = this._policyCategoryToResolutionIdsMap[category];
	        if (
		        map.exclude_resolution_ids.includes(resolutionId) ||
		        !map.include_resolution_ids.includes(resolutionId)
	        ) {
		        continue;
	        }
            return category;
        }
        return undefined;
    }

    public get policyList(): PolicyStore[] {
        return this._policyRuleList;
    }

    public get selectedPolicy(): PolicyStore | undefined {
        return this.policyList.find((p) =>
            p.isIdEquals(this._selectedPloicyId)
        );
    }

    private _selectedPloicyId?: string | number;

    public setSelectedPolicyId(policyId: string | number | undefined) {
        runInAction(() => {
            this._selectedPloicyId = policyId;
        });
    }

    public get displayAsDisabledRefundPolicy(): PolicyStore | undefined {
        return this.policyList.find((policy) => policy.displayAsDisabled);
    }

    private get _firstAvailableCoverageReturnMethod():
        | IReturnMethodPreset
        | undefined {
        const coverageReturnMethodsList =
            this._settings?.return_methods?.filter((rm) => {
                const aggCode =
                    rm.return_label_setting?.shipping_connection
                        .aggregator_code;
                if (!aggCode) {
                    return false;
                }
                return this._aggregatorsInfoStore?.isCoverage(aggCode);
            });

        if (!coverageReturnMethodsList?.length) {
            return undefined;
        }

        return coverageReturnMethodsList.reduce((acc, method) => {
            return acc.id < method.id ? acc : method;
        }, coverageReturnMethodsList[0]);
    }

    public addPolicy(policy?: PolicyStore | undefined): number | string {
        const clientId = uuid();
        let changes: IPolicyRuleChanges = {
            _client_id: clientId,
            item_ineligibility_reason_key: buildPolicyItemIneligibilityReasonClientKey(clientId),
            ...this._getDefaultPolicyModel(),
        };

        /** Auto-select the first RCRM if available and set the rule as coverage*/
        if (this._firstAvailableCoverageReturnMethod) {
            changes.return_method_id =
                this._firstAvailableCoverageReturnMethod.id;
            changes.is_coverage = true;
        }

        if (policy) {
            const sourcePolicy = policy.getUpdatedModel();
            changes = {
                ...changes,
                ...sourcePolicy,
                _client_id: clientId,
                ...buildDuplicatedPolicyLocalizationKeys(
                    clientId,
                    sourcePolicy
                ),
            };
        }
        if (!changes.apply_on) {
            changes.apply_on = APPLY_ON.ALL_EXCEPT_GIFTS;
        }

        const newPolicy = new PolicyStore(this, {} as IPolicyRule);
        newPolicy.setChanges(changes);
        this.policyList.push(newPolicy);

        return newPolicy.id;
    }

    private _getDefaultPolicyModel(): IPolicyRuleChanges {

		const {
			isCustomerAddressValidationRequirementSupported,
			isCustomerAddressPrefillSupported
		} = dashboardSettingsStore.resolveCapability(PROFILE_CAPABILITIES.RETURN_POLICY_RULE);

		return {
            name: "Policy rule " + (this.policyList.length + 1),
            resolution_id: undefined,
            return_method_id: undefined,
            is_enabled: false,
            display_as_disabled: false,
            apply_on: APPLY_ON.ALL_EXCEPT_GIFTS,
            is_auto_approved: APPROVAL_PROCESS.MANUAL,
            auto_mark_done: AUTO_MARK_DONE.MANUALLY,
            auto_mark_done_trigger: null,
            auto_mark_done_execute: null,
            auto_mark_done_since_value: 1,
            auto_mark_done_since_unit: SINCE_UNIT.DAYS,

            is_auto_exchange: false,
            auto_exchange_trigger: AUTO_PROCESS_TRIGGER.SHIPMENT_RECEIVED,
            auto_exchange_execute: AUTO_PROCESS_EXECUTE.SINCE_TRIGGERED,
            auto_exchange_since_value: 1,
            auto_exchange_since_unit: SINCE_UNIT.DAYS,
            auto_exchange_validated_qualifier:
                AUTO_PROCESS_VALIDATED_QUALIFIER.EXECUTE_WHEN_ALL_RESTOCKABLE,
            is_auto_refund: false,
            auto_refund_trigger: null,
            auto_refund_execute: null,
            auto_refund_since_value: 1,
            auto_refund_since_unit: SINCE_UNIT.DAYS,
            auto_refund_validated_qualifier:
                AUTO_PROCESS_VALIDATED_QUALIFIER.EXECUTE_WHEN_ALL_RESTOCKABLE,
            include_shipping_amount_in_refund: false,
            charge_fees: CHARGE_FEES.DO_NOT_CHARGE_FEE,
            require_address: isCustomerAddressValidationRequirementSupported,
            prefill_address: isCustomerAddressPrefillSupported,
            address_requirements: this.getDefaultAddressRequirements(),
            limit_eligible_items_order_level: true,
            limit_eligible_items_customer_level: false,
            limit_eligible_items_rma_level: false,
            limit_eligible_items: false,
            policy_rule_condition_groups_order_level: [
                {
                    is_eligible: true,
                    conditions_operator: CONDITION_GROUP_OPERATOR.OP_AND,
                    policy_rule_conditions: [
                        {
                            field: "fulfilled",
                            operator: CONDITION_OPERATORS.OP_SMALLER_OR_EQUAL,
                            compare_value: JSON.stringify({
                                value: 30,
                                type: "days",
                            }),
                        } as IPolicyCondition,
                    ],
                } as IPolicyConditionGroup,
            ],
            policy_rule_condition_groups_customer_level: [],
            policy_rule_condition_groups_rma_level: [],
            policy_rule_condition_groups: [],
            return_label_fee_payment:
                RETURN_LABEL_FEE_PAYMENT.DEDUCT_FROM_REFUNDS,
            enable_reserve_items: false,
            item_reservation_timeframe: null,
            restock_time: null,
            display_fee_option: DISPLAY_FEE_OPTION.INCLUDE_ZERO_FREE,
            policy_rule_fees: [],
            instant_exchange_enabled: true,
            capture_payment_trigger: AUTO_PROCESS_TRIGGER.USE_DEFAULT_TRIGGER,
            capture_payment_validated_qualifier: null,
            policy_rule_availability: [
                { used_for: USED_FOR_OPTIONS.USED_FOR_PORTAL },
                { used_for: USED_FOR_OPTIONS.USED_FOR_CRMA_ONLINE_USERS },
                {
                    used_for:
                        USED_FOR_OPTIONS.USED_FOR_CRMA_PHYSICAL_STORE_USERS,
                },
                { used_for: USED_FOR_OPTIONS.USED_FOR_CRMA_VIA_RETURNGO_API },
            ],
        };
    }

    private getDefaultAddressRequirements(): IPolicyAddressRequirements {
        return {
            email: {
                display: true,
                required: true,
            },
            phone: {
                display: true,
                required: true,
            },
            country: {
                display: true,
                required: true,
            },
            state: {
                display: true,
                required: true,
            },
            address_line: {
                display: true,
                required: true,
            },
            apartment: {
                display: true,
            },
            zip: {
                display: true,
                required: false,
            },
            city: {
                display: true,
                required: true,
            },
        };
    }

    public discardPolicy(policy: PolicyStore): void {
        if (!policy.isExisting) {
	        rmsMixpanelStore.trackEvent(POLICY_RULES_MIXPANEL_DROP_ADD_RULE);
        }
        const policyIndex = this.policyList.indexOf(policy);

        if (policyIndex >= 0) {
            this.policyList.splice(policyIndex, 1);
        } else {
            /** should never happen */
            console.error("discardPolicy did not find the store to remove");
        }
    }

    async load() {
        this._isLoading = true;
        this._isLoaded = false;
        try {
            const { policy_rules, settings } =
                await this.policyService.getPolicyRuleList();
            runInAction(() => {
                this._policyRuleList = [];
                this._settings = settings;
                this._aggregatorsInfoStore = new AggregatorsInfoStore(
                    settings.aggregators_info
                );
                policy_rules.forEach((policyRule) => {
                    try {
                        this._policyRuleList.push(
                            new PolicyStore(this, policyRule)
                        );
                    } catch (error) {
                        console.error(
                            `Failed to load policy rule ${policyRule.id} (${policyRule.name})`,
                            error
                        );
                    }
                });
                this._isLoading = false;
                this._isLoaded = true;
            });
        } catch (e) {
            console.error(e);
            runInAction(() => {
                this._isLoading = false;
            });
        }
    }

    public getRefundToPaymentMethodNoteData(): {
        type: NOTE_TYPE;
        bodyType: REFUND_POLICIES_NOTE_TYPE;
    } | null {
        const showDisabledResolution =
            currentPortalStore.setting?.showDisabledResolution;

        if (!showDisabledResolution) {
            return null;
        }

        let bodyType: REFUND_POLICIES_NOTE_TYPE | null = null;
        const refundToPaymentPolicies = this.policyList.filter((policy) => {
            return (
                policy.isEnabled &&
                policy.resolution?.resolution_type === RESOLUTION_TYPE.Refund
            );
        });
        const hasDisplayAsDisabledPolicy = !!refundToPaymentPolicies.find(
            (policy) => policy.displayAsDisabled
        );

        if (refundToPaymentPolicies.length > 0 && !hasDisplayAsDisabledPolicy) {
            bodyType = REFUND_POLICIES_NOTE_TYPE.SET_DISPLAY_AS_DISABLED;
        }

        if (refundToPaymentPolicies.length === 0) {
            bodyType =
                REFUND_POLICIES_NOTE_TYPE.CREATE_REFUND_TO_PAYMENT_METHOD;
        }

        if (!bodyType) {
            return null;
        }

        return {
            type: NOTE_TYPE.WARNING,
            bodyType: bodyType,
        };
    }

    public hasEnabledPolicies(): boolean {
        return this.policyList.filter((p) => p.isEnabled).length > 0;
    }
}

export default new PolicyListStore();
